import React, { useMemo, useState } from "react";
import "@styles/pages/MemberZone/PointsHistory.scss";
import { useSelector } from "react-redux";
import { Box, Typography, Popover, Button, Pagination } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AccordionRow from "@components/ui/AccordionRow";
import HealthBar from "@components/ui/HealthBar";
import AnimatedNumber from "@components/ui/AnimatedNumber";

const PointsHistory = () => {
  const { t } = useTranslation();

  const pointHistoryList = useSelector(
    (state) => state.memberPoint.pointHistory
  );

  const [selectedType, setSelectedType] = useState("all");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const filteredList = useMemo(() => {
    if (selectedType === "all") {
      return pointHistoryList?.point_history;
    }
    if (selectedType === "earned") {
      return pointHistoryList?.point_history.filter(
        (item) =>
          item.point_type === 1 ||
          item.point_type === 2 ||
          item.point_type === 5 ||
          item.point_type === 6
      );
    }
    if (selectedType === "used") {
      return pointHistoryList?.point_history.filter(
        (item) => item.point_type === 3 || item.point_type === 4
      );
    }
    if (selectedType === "expired") {
      return pointHistoryList?.point_history.filter(
        (item) => item.point_type === 7
      );
    }
    return pointHistoryList;
  }, [selectedType, pointHistoryList]);

  const totalPages = Math.ceil(filteredList?.length / itemsPerPage);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleFilterClick = (type) => {
    setSelectedType(type);
    setPage(1); // 重設頁數
  };

  const memberInfoData = useSelector(
    (state) => state.memberInfo.memberInfoData
  );

  // const totalPoints = pointHistoryList?.point_balance?.reduce(
  //   (sum, item) => sum + (item.point || 0),
  //   0
  // );

  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "point-expiry-popover" : undefined;

  const typeLabelMap = {
    1: t("member:consumption"),
    2: t("member:birthday_bonus"),
    3: t("member:redemption"),
    4: t("member:samsung_rewards_point_conversion"),
    5: t("member:promotional_bonus_point"),
    6: t("member:point_adjustment"),
    7: t("member:expiry"),
  };

  const getSDollarKey = (pointType) => {
    switch (pointType) {
      case 1:
      case 2:
      case 5:
      case 6:
        return t("member:earned_s_dollar");
      case 3:
      case 4:
        return t("member:used_s_dollar");
      case 7:
        return t("member:expired_s_dollar");

      default:
        return t("member:earned_s_dollar"); // fallback
    }
  };

  return (
    <Box className="points-histroy-container">
      {/* === Popover === */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "point-expiry-popover" }}
      >
        <Typography className="popover-title">
          <Trans i18nKey="member:point_expiry_summary" />
        </Typography>

        {/* Header row */}
        <Box className="popover-header">
          <Typography className="date">
            <Trans i18nKey="member:expiry_date" />
          </Typography>
          <Typography className="point">
            <Trans i18nKey="member:point_history" />
          </Typography>
        </Box>

        {/* Data rows */}
        {pointHistoryList?.old_point_wallet?.point_expire?.map(
          (item, index) => (
            <Box key={index} className="popover-row">
              <Typography className="date">
                {dayjs(item.expiry_date).format("YYYY-MM-DD")}
              </Typography>
              <Typography className="point">
                {item.point?.toLocaleString()}
              </Typography>
            </Box>
          )
        )}
      </Popover>

      <Box className="points-balance-container">
        <Box className="balance-container">
          <Typography className="balance-label">
            <Trans i18nKey="member:your_s_dollar_balance" />
          </Typography>
          <Box className="balance-value-container">
            <img
              className="balance-icon"
              src={`${process.env.PUBLIC_URL}/assets/images/member/s_dollar.png`}
            />
            <Typography className="balance-value">
              <AnimatedNumber value={pointHistoryList?.total_balance} />
            </Typography>
          </Box>
          {pointHistoryList?.new_point_wallet != null && (
            <Typography
              className="balance-label-s"
              dangerouslySetInnerHTML={{
                __html: t("member:new_s_dollar_expiry_note", {
                  s_dollar: pointHistoryList?.new_point_wallet?.balance.toLocaleString(),
                  expiry_date:
                    pointHistoryList?.new_point_wallet?.point_cycle_end,
                }),
              }}
            />
          )}
          {pointHistoryList?.old_point_wallet?.balance > 0 && (
            <Typography className="balance-label-s">
              <span
                className="balance-label-s"
                dangerouslySetInnerHTML={{
                  __html: t("member:old_s_dollar_expiry_note", {
                    s_dollar: pointHistoryList?.old_point_wallet?.balance.toLocaleString(),
                  }),
                }}
              />
              <span
                onClick={handleOpen}
                style={{ cursor: "pointer", color: "#1976d2", marginLeft: 4 }}
              >
                <Trans i18nKey="member:view_details" />
              </span>
            </Typography>
          )}
        </Box>
        <Box className="balance-container">
          {pointHistoryList?.new_point_wallet != null && (
            <>
              <Typography
                className="balance-bar-cycle"
                dangerouslySetInnerHTML={{
                  __html: t("member:s_dollar_point_cycle", {
                    start_date:
                      pointHistoryList?.new_point_wallet?.point_cycle_start,
                    end_date:
                      pointHistoryList?.new_point_wallet?.point_cycle_end,
                  }),
                }}
              />{" "}
              <Box className="balance-bar-container">
                <Box className="balance-bar-text-container">
                  <Typography>
                    <Trans i18nKey="member:new_s_dollar_bar" />
                  </Typography>
                  <Typography>
                    <AnimatedNumber
                      value={pointHistoryList?.new_point_wallet?.point_used}
                    />{" "}
                    /{" "}
                    {pointHistoryList?.new_point_wallet?.point_use_ceiling.toLocaleString()}
                  </Typography>
                </Box>
                <HealthBar
                  value={pointHistoryList?.new_point_wallet?.point_used}
                  max={pointHistoryList?.new_point_wallet?.point_use_ceiling}
                />
                {pointHistoryList?.old_point_wallet?.balance > 0 && (
                  <Typography className="bar-notice">
                    <Trans i18nKey="member:new_s_dollar_bar_notice" />
                  </Typography>
                )}
              </Box>
            </>
          )}
          <br />
          {pointHistoryList?.new_point_wallet != null && (
            <Box className="balance-bar-container">
              <Box className="balance-bar-text-container">
                <Typography>
                  <Trans i18nKey="member:smasung_rewards_bar" />
                </Typography>
                <Typography>
                  <AnimatedNumber
                    value={pointHistoryList?.new_point_wallet?.reward_converted}
                  />{" "}
                  /{" "}
                  {pointHistoryList?.new_point_wallet?.reward_convert_ceiling.toLocaleString()}
                </Typography>
              </Box>
              <HealthBar
                value={pointHistoryList?.new_point_wallet?.reward_converted}
                max={pointHistoryList?.new_point_wallet?.reward_convert_ceiling}
                valueColor="#f5b623"
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box className="points-histroy-list-container">
        <Typography className="title">
          <Trans i18nKey="member:point_history" />
        </Typography>
        <Box className="filter-container">
          <Button
            className={selectedType === "all" ? "loading-button" : "button"}
            onClick={() => handleFilterClick("all")}
          >
            <Trans i18nKey="member:type_button_all" />
          </Button>
          <Button
            className={selectedType === "earned" ? "loading-button" : "button"}
            onClick={() => handleFilterClick("earned")}
            sx={{ ml: 1 }}
          >
            <Trans i18nKey="member:type_button_earned_s_dollar" />
          </Button>
          <Button
            className={selectedType === "used" ? "loading-button" : "button"}
            onClick={() => handleFilterClick("used")}
            sx={{ ml: 1 }}
          >
            <Trans i18nKey="member:type_button_used_s_dollar" />
          </Button>
          <Button
            className={selectedType === "expired" ? "loading-button" : "button"}
            onClick={() => handleFilterClick("expired")}
            sx={{ ml: 1 }}
          >
            <Trans i18nKey="member:type_button_expired_s_dollar" />
          </Button>
        </Box>
        {filteredList?.map((item, index) => (
          <AccordionRow
            key={index}
            summaryTitle={getSDollarKey(item.point_type)}
            summaryValue={item.product_name}
            summaryRight={`${item.point.toLocaleString()} S dollar`}
            details={[
              {
                label: t("member:type"),
                value: typeLabelMap[item.point_type] || "-",
              },
              {
                label: t("member:register_or_use_date"),
                value: dayjs(item.point_date).format("YYYY-MM-DD"),
              },
            ]}
          />
        ))}
        {/* Pagination 加在底部 */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PointsHistory;
